import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { t } from "ttag";

import type { AccordionSectionBlock as AccordionSectionBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

/**
 * Faithful port of the legacy server-rendered ``cms/blocks/accordion-block.html``
 * (the tempurpedic-local AccordionSectionBlock).
 *
 * Each section owns its open/close state (the AccordionBlock precedent): a
 * click on the ``.accordion`` anchor toggles ``accordion--is-active`` on the
 * trigger, ``accordion-target--is-active`` on the panel, and their
 * ``aria-expanded`` / ``aria-hidden`` flags.
 *
 * Per-section element ids / ``aria-controls`` are ``accordion-panel-{header
 * slugified}-{1-based counter}``, computed server-side (Django ``slugify``) and
 * delivered as ``section.panel_id`` (see ``accordion.py``). Sections render
 * collapsed by default (the legacy ``value.is_expanded`` field does not exist).
 * ``block_header`` / ``header`` are plain CharBlock text (auto-escaped by React,
 * matching ``{{ }}``); ``body`` is the RichTextBlock value already wrapped in
 * ``<div class="rich-text">`` (the reactivated serializer pre-wraps it, matching
 * ``{{ section.body }}``), injected directly inside ``.accordion-target`` so it
 * is the wrapper's direct child.
 */
type Section = NonNullable<AccordionSectionBlockValue["sections"]>[number];

const AccordionSection = (
    props: Immutable<{ section: Section }>,
): React.JSX.Element => {
    const section = props.section;
    const [isExpanded, setIsExpanded] = React.useState(false);
    return (
        <li className="accordion__list-item">
            <a
                href="#"
                title={t`Toggle Customer Service Dropdown`}
                className={concatClassNames([
                    "accordion",
                    isExpanded ? "accordion--is-active" : "",
                ])}
                aria-controls={section.panel_id}
                aria-expanded={isExpanded ? "true" : "false"}
                onClick={(event) => {
                    event.preventDefault();
                    setIsExpanded((value) => !value);
                }}
            >
                {section.header}
            </a>
            <div
                id={section.panel_id}
                className={concatClassNames([
                    "accordion-target",
                    isExpanded ? "accordion-target--is-active" : "",
                ])}
                aria-hidden={isExpanded ? "false" : "true"}
                dangerouslySetInnerHTML={{
                    __html: section.body ?? "",
                }}
            />
        </li>
    );
};

export const AccordionSectionBlock = (
    props: Immutable<{ value: AccordionSectionBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    return (
        <div
            className={concatClassNames([
                "accordion__container",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <h2 className="accordion__title">{v.block_header}</h2>

            <ul className="accordion__list">
                {(v.sections ?? []).map((section, i) => (
                    <AccordionSection key={i} section={section} />
                ))}
            </ul>
        </div>
    );
};
